<template>
  <div class="vsdc-settings">
    <hs-alert
      v-if="error"
      tone="danger"
      :message="error"
    />

    <section class="vsdc-card">
      <div class="vsdc-card__header">
        <div>
          <h2>VSDC Server</h2>
          <p>Global endpoint used by every store and background EBM task.</p>
        </div>
        <div class="vsdc-actions">
          <hs-badge :tone="setting?.source === 'system_setting' ? 'success' : 'warning'">
            {{ sourceLabel }}
          </hs-badge>
          <hs-button
            variant="ghost"
            size="sm"
            icon="refresh-cw"
            :loading="loading"
            @click="loadAll"
          >
            Refresh
          </hs-button>
        </div>
      </div>

      <div class="vsdc-current">
        <span>Active endpoint</span>
        <code>{{ setting?.value || "Not configured" }}</code>
      </div>

      <form class="vsdc-form" @submit.prevent="requestChange">
        <hs-input
          v-model.trim="proposal"
          label="Proposed endpoint"
          placeholder="https://vsdc.example.com/rraVsdcProd3.0.8"
          hint="Absolute HTTP(S) URL without credentials, query parameters, or fragments."
          :error="proposalError"
          required
        />
        <hs-textarea
          v-model.trim="justification"
          label="Justification"
          placeholder="Explain why the VSDC endpoint must change."
          :error="justificationError"
          :rows="3"
          required
        />
        <div class="vsdc-form__footer">
          <span>A different super admin must approve this change.</span>
          <hs-button
            type="submit"
            icon="send"
            :loading="requesting"
          >
            Request change
          </hs-button>
        </div>
      </form>
    </section>

    <section class="vsdc-card">
      <div class="vsdc-card__header">
        <div>
          <h2>Pending approvals</h2>
          <p>Approve or reject requests, then apply approved changes.</p>
        </div>
        <hs-badge tone="primary">{{ approvals.length }}</hs-badge>
      </div>

      <div v-if="!approvals.length" class="vsdc-empty">
        No active VSDC endpoint approvals.
      </div>
      <article
        v-for="approval in approvals"
        :key="approval.id"
        class="vsdc-approval"
      >
        <div class="vsdc-approval__summary">
          <div>
            <strong>{{ approvalTitle(approval) }}</strong>
            <code>{{ approvalTarget(approval) }}</code>
          </div>
          <hs-badge :tone="approval.status === 'APPROVED' ? 'success' : 'warning'">
            {{ approval.status }}
          </hs-badge>
        </div>
        <div class="vsdc-approval__meta">
          Requested by user #{{ approval.requested_by_id }}
          <span>·</span>
          {{ formatDate(approval.created_at) }}
        </div>
        <hs-input
          v-if="approval.status === 'PENDING'"
          v-model.trim="approvalComments[approval.id]"
          label="Decision comment"
          placeholder="Reason for approval or rejection"
        />
        <div class="vsdc-approval__actions">
          <template v-if="approval.status === 'PENDING'">
            <span v-if="isOwnRequest(approval)" class="vsdc-own-request">
              The requester cannot approve this request.
            </span>
            <template v-else>
              <hs-button
                variant="success"
                size="sm"
                icon="check"
                :loading="busyApprovalId === approval.id"
                @click="decideApproval(approval, 'approve')"
              >
                Approve
              </hs-button>
              <hs-button
                variant="danger"
                size="sm"
                icon="x"
                :loading="busyApprovalId === approval.id"
                @click="decideApproval(approval, 'reject')"
              >
                Reject
              </hs-button>
            </template>
          </template>
          <hs-button
            v-else-if="approval.status === 'APPROVED'"
            variant="primary"
            size="sm"
            icon="play"
            :loading="busyApprovalId === approval.id"
            @click="applyApproval(approval)"
          >
            Apply approved {{ isRollback(approval) ? "rollback" : "change" }}
          </hs-button>
        </div>
      </article>
    </section>

    <section class="vsdc-card">
      <div class="vsdc-card__header">
        <div>
          <h2>Revision history</h2>
          <p>Review previous endpoints and request an approved rollback.</p>
        </div>
      </div>

      <div v-if="!history.length" class="vsdc-empty">
        No VSDC endpoint revisions have been recorded.
      </div>
      <article
        v-for="revision in history"
        :key="revision.id"
        class="vsdc-revision"
      >
        <div>
          <div class="vsdc-revision__heading">
            <strong>Revision {{ revision.revision }}</strong>
            <hs-badge v-if="revision.is_active" tone="success">Active</hs-badge>
          </div>
          <code>{{ revision.value || "Not configured" }}</code>
          <span>
            {{ revision.change_type || "UPDATE" }} ·
            {{ formatDate(revision.created_at) }} ·
            {{ revision.actor?.names || `User #${revision.recorded_by_id || "unknown"}` }}
          </span>
        </div>
        <hs-button
          v-if="!revision.is_active"
          variant="secondary"
          size="sm"
          icon="rotate-ccw"
          @click="selectRollback(revision)"
        >
          Request rollback
        </hs-button>
      </article>

      <form
        v-if="rollbackRevision"
        class="vsdc-rollback"
        @submit.prevent="requestRollback"
      >
        <hs-alert
          tone="warning"
          :message="`Request rollback to revision ${rollbackRevision.revision}: ${rollbackRevision.value}`"
        />
        <hs-textarea
          v-model.trim="rollbackJustification"
          label="Rollback justification"
          placeholder="Explain why this endpoint should be restored."
          :error="rollbackError"
          :rows="3"
          required
        />
        <div class="vsdc-rollback__actions">
          <hs-button variant="ghost" @click="cancelRollback">Cancel</hs-button>
          <hs-button
            type="submit"
            variant="danger"
            icon="rotate-ccw"
            :loading="requestingRollback"
          >
            Request rollback
          </hs-button>
        </div>
      </form>
    </section>
  </div>
</template>

<script>
import { toast } from "vue3-toastify";
import api from "@/helper/api";
import http from "@/helper/http";
import {
  HsAlert,
  HsBadge,
  HsButton,
  HsInput,
  HsTextarea,
} from "@/components/ui";

const SETTING_KEY = "rra_ebm_client_api_root_url";

export default {
  name: "VsdcServerSettings",
  components: {
    HsAlert,
    HsBadge,
    HsButton,
    HsInput,
    HsTextarea,
  },
  props: {
    currentUserId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      setting: null,
      approvals: [],
      history: [],
      proposal: "",
      justification: "",
      proposalError: "",
      justificationError: "",
      approvalComments: {},
      rollbackRevision: null,
      rollbackJustification: "",
      rollbackError: "",
      error: "",
      loading: false,
      requesting: false,
      requestingRollback: false,
      busyApprovalId: null,
    };
  },
  computed: {
    sourceLabel() {
      if (!this.setting?.source) return "Not configured";
      return this.setting.source === "system_setting"
        ? "Dynamic setting"
        : "Environment fallback";
    },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    async loadAll() {
      this.loading = true;
      this.error = "";
      try {
        const [settingResponse, approvalsResponse, historyResponse] =
          await Promise.all([
            http.get(api.vsdc_server_setting),
            http.get(api.runtime_setting_approvals),
            http.get(api.vsdc_server_setting_history),
          ]);
        this.setting = settingResponse.data;
        if (!this.proposal) this.proposal = this.setting?.value || "";
        this.approvals = (
          approvalsResponse.data?.approval_requests || []
        ).filter((approval) => (
          approval.resource_id === SETTING_KEY &&
          ["PENDING", "APPROVED"].includes(approval.status)
        ));
        this.history = historyResponse.data?.items || [];
      } catch (error) {
        this.error = this.errorMessage(error, "Unable to load VSDC server settings.");
      } finally {
        this.loading = false;
      }
    },
    normalizeUrl(value) {
      const rawValue = String(value || "").trim();
      let parsed;
      try {
        parsed = new URL(rawValue);
      } catch (_error) {
        throw new Error("Enter an absolute HTTP(S) URL.");
      }
      if (!["http:", "https:"].includes(parsed.protocol)) {
        throw new Error("Only HTTP and HTTPS URLs are supported.");
      }
      if (parsed.username || parsed.password) {
        throw new Error("The URL must not contain credentials.");
      }
      if (parsed.search || parsed.hash) {
        throw new Error("The URL must not contain a query or fragment.");
      }
      return rawValue.replace(/\/+$/, "");
    },
    validateChange() {
      this.proposalError = "";
      this.justificationError = "";
      try {
        this.proposal = this.normalizeUrl(this.proposal);
      } catch (error) {
        this.proposalError = error.message;
      }
      if (this.justification.length < 8) {
        this.justificationError = "Provide at least 8 characters of justification.";
      }
      return !this.proposalError && !this.justificationError;
    },
    async requestChange() {
      if (!this.validateChange()) return;
      this.requesting = true;
      this.error = "";
      try {
        await http.post(api.vsdc_server_setting_change_request, {
          proposed_value: this.proposal,
          justification: this.justification,
          expires_after_minutes: 720,
        });
        this.justification = "";
        toast.success("VSDC endpoint change submitted for approval.");
        await this.loadAll();
      } catch (error) {
        this.error = this.errorMessage(error, "Unable to request the VSDC endpoint change.");
      } finally {
        this.requesting = false;
      }
    },
    isOwnRequest(approval) {
      return String(approval.requested_by_id) === String(this.currentUserId);
    },
    isRollback(approval) {
      return approval.payload?.operation === "rollback";
    },
    approvalTitle(approval) {
      return this.isRollback(approval)
        ? `Rollback to revision ${approval.payload?.target_revision || ""}`
        : "VSDC endpoint change";
    },
    approvalTarget(approval) {
      return this.isRollback(approval)
        ? approval.payload?.target_value || "Previous value"
        : approval.payload?.proposed_value || "New value";
    },
    async decideApproval(approval, decision) {
      this.busyApprovalId = approval.id;
      this.error = "";
      try {
        const endpoint = decision === "approve"
          ? api.approve_request(approval.id)
          : api.reject_request(approval.id);
        await http.post(endpoint, {
          comment: this.approvalComments[approval.id] || undefined,
        });
        toast.success(`VSDC endpoint request ${decision}d.`);
        await this.loadAll();
      } catch (error) {
        this.error = this.errorMessage(error, `Unable to ${decision} the request.`);
      } finally {
        this.busyApprovalId = null;
      }
    },
    async applyApproval(approval) {
      this.busyApprovalId = approval.id;
      this.error = "";
      try {
        await http.post(
          this.isRollback(approval)
            ? api.vsdc_server_setting_rollback_apply
            : api.vsdc_server_setting_apply,
          {
            approval_id: approval.id,
            run_id: `hovastore-dashboard-${Date.now()}`,
          },
        );
        toast.success(
          this.isRollback(approval)
            ? "VSDC endpoint rollback applied."
            : "VSDC endpoint change applied.",
        );
        this.proposal = "";
        await this.loadAll();
      } catch (error) {
        this.error = this.errorMessage(error, "Unable to apply the approved request.");
      } finally {
        this.busyApprovalId = null;
      }
    },
    selectRollback(revision) {
      this.rollbackRevision = revision;
      this.rollbackJustification = "";
      this.rollbackError = "";
    },
    cancelRollback() {
      this.rollbackRevision = null;
      this.rollbackJustification = "";
      this.rollbackError = "";
    },
    async requestRollback() {
      this.rollbackError = "";
      if (this.rollbackJustification.length < 8) {
        this.rollbackError = "Provide at least 8 characters of justification.";
        return;
      }
      this.requestingRollback = true;
      this.error = "";
      try {
        await http.post(api.vsdc_server_setting_rollback_request, {
          history_id: this.rollbackRevision.id,
          justification: this.rollbackJustification,
          expires_after_minutes: 720,
        });
        this.cancelRollback();
        toast.success("VSDC endpoint rollback submitted for approval.");
        await this.loadAll();
      } catch (error) {
        this.error = this.errorMessage(error, "Unable to request the rollback.");
      } finally {
        this.requestingRollback = false;
      }
    },
    formatDate(value) {
      if (!value) return "Unknown time";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? "Unknown time" : date.toLocaleString();
    },
    errorMessage(error, fallback) {
      return (
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.message ||
        fallback
      );
    },
  },
};
</script>

<style scoped>
.vsdc-settings {
  display: grid;
  gap: 1rem;
}

.vsdc-card {
  background: var(--hs-surface, #fff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 18px;
  display: grid;
  gap: 1rem;
  padding: 1.15rem;
}

.vsdc-card__header,
.vsdc-approval__summary,
.vsdc-revision,
.vsdc-form__footer,
.vsdc-rollback__actions {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
}

.vsdc-card h2 {
  color: var(--hs-text, #0f172a);
  font-size: 1rem;
  margin: 0;
}

.vsdc-card p,
.vsdc-form__footer span,
.vsdc-approval__meta,
.vsdc-revision span {
  color: var(--hs-muted, #64748b);
  font-size: 0.78rem;
  margin: 0.25rem 0 0;
}

.vsdc-actions,
.vsdc-approval__actions,
.vsdc-revision__heading {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
}

.vsdc-current,
.vsdc-approval,
.vsdc-revision {
  background: color-mix(in srgb, var(--hs-bg-soft, #f8fafc) 72%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  padding: 0.9rem;
}

.vsdc-current {
  display: grid;
  gap: 0.35rem;
}

.vsdc-current span {
  color: var(--hs-muted, #64748b);
  font-size: 0.75rem;
  font-weight: 800;
}

.vsdc-card code {
  color: var(--hs-text, #0f172a);
  font-size: 0.82rem;
  overflow-wrap: anywhere;
}

.vsdc-form,
.vsdc-rollback {
  display: grid;
  gap: 0.9rem;
}

.vsdc-approval,
.vsdc-revision > div {
  display: grid;
  gap: 0.45rem;
}

.vsdc-own-request,
.vsdc-empty {
  color: var(--hs-muted, #64748b);
  font-size: 0.8rem;
}

@media (max-width: 720px) {
  .vsdc-card__header,
  .vsdc-approval__summary,
  .vsdc-revision,
  .vsdc-form__footer,
  .vsdc-rollback__actions {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
